import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { useCreateTrackerMutation } from '../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { detectTracker, TrackerDetectionKind } from './detectTracker';
import { SupportedTrackerChips } from './SupportedTrackerChips';
import { SnippetStatusBar } from './SnippetStatusBar';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './SMLTrackers.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AddTrackerModalProps {
  isOpen: boolean;
  accountId: number;
  closeModal: () => void;
}

/**
 * The same paste box as before — it now says what it found.
 *
 * The snippet still goes to the server whole; the reading here is the artist's
 * receipt. Create stays shut until the reading succeeds, so a mangled paste
 * fails in front of the artist instead of silently never firing.
 */
const AddTrackerModalComponent: React.FC<AddTrackerModalProps> = ({ isOpen, accountId, closeModal }) => {
  const intl = useIntl();
  const [snippet, setSnippet] = useState('');
  const [createTracker, { isLoading, error, reset }] = useCreateTrackerMutation();

  const detection = useMemo(() => detectTracker(snippet), [snippet]);
  const isRecognised = detection.kind === TrackerDetectionKind.recognised;
  const isRefused =
    detection.kind === TrackerDetectionKind.unsupported || detection.kind === TrackerDetectionKind.unreadable;

  const onSnippetChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => setSnippet(event.target.value),
    []
  );

  const onClose = useCallback(() => {
    setSnippet('');
    reset();
    closeModal();
  }, [closeModal, reset]);

  const onCreate = useCallback(() => {
    if (!isRecognised) {
      return;
    }
    createTracker({ accountId, snippet })
      .then((result) => {
        if (!isSuccessfulResult(result)) {
          return;
        }
        onClose();
      })
      .catch(console.error);
  }, [accountId, createTracker, isRecognised, onClose, snippet]);

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered className="sml-modal">
      <ModalHeader toggle={onClose}>
        {intl.formatMessage({ id: 'app.sml.trackers.add', defaultMessage: 'Add tracker' })}
      </ModalHeader>
      <ModalBody>
        {error !== undefined && <ErrorAlert error={error} />}
        <div className={styles.modalBody}>
          <div className={styles.recognise}>
            <span className={styles.supportedLabel}>
              {intl.formatMessage({ id: 'app.sml.trackers.recognise', defaultMessage: 'We recognise:' })}
            </span>
            <SupportedTrackerChips hitType={isRecognised ? detection.type : undefined} />
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {intl.formatMessage({ id: 'app.sml.trackers.snippet.label', defaultMessage: 'Snippet' })}
            </span>
            <div className={clsx(styles.paste, isRecognised && styles.pasteGood, isRefused && styles.pasteBad)}>
              <textarea
                className={styles.textarea}
                value={snippet}
                onChange={onSnippetChange}
                disabled={isLoading}
                spellCheck={false}
                aria-label={intl.formatMessage({ id: 'app.sml.trackers.snippet.label', defaultMessage: 'Snippet' })}
                placeholder={intl.formatMessage({
                  id: 'app.sml.trackers.snippet.placeholder',
                  defaultMessage: 'Paste the snippet from your tracking provider',
                })}
              />
              <SnippetStatusBar detection={detection} />
            </div>
            <span className={clsx(styles.helper, isRefused && styles.helperBad)}>
              {isRefused &&
                intl.formatMessage({
                  id: 'app.sml.trackers.snippet.help.refused',
                  defaultMessage: 'Only Facebook Pixel, Google Analytics and Plausible can be added.',
                })}
              {isRecognised &&
                intl.formatMessage({
                  id: 'app.sml.trackers.snippet.help.recognised',
                  defaultMessage:
                    "The provider's own tag is loaded with this ID, so the pasted code can't break your pages.",
                })}
              {detection.kind === TrackerDetectionKind.empty &&
                intl.formatMessage({
                  id: 'app.sml.trackers.snippet.help.default',
                  defaultMessage: "We read the ID out of the snippet and load the provider's own tag with it.",
                })}
            </span>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <span className={styles.footerNote}>
          {isRefused
            ? intl.formatMessage({
                id: 'app.sml.trackers.modal.note.refused',
                defaultMessage: 'Nothing is saved until a snippet is recognised.',
              })
            : intl.formatMessage({
                id: 'app.sml.trackers.modal.note',
                defaultMessage: 'Applies to every smartlink page in your account.',
              })}
        </span>
        <SMLButton outline onClick={onClose} disabled={isLoading}>
          {intl.formatMessage({ id: 'app.forms.buttons.cancel.label', defaultMessage: 'Cancel' })}
        </SMLButton>
        <SMLButton onClick={onCreate} disabled={!isRecognised || isLoading} isLoading={isLoading}>
          {intl.formatMessage({ id: 'app.forms.buttons.create', defaultMessage: 'Create' })}
        </SMLButton>
      </ModalFooter>
    </Modal>
  );
};

export const AddTrackerModal = TypedMemo(AddTrackerModalComponent);
